import { QuestionnaireCategoryItemsItemOneOf } from '@/api/generated-catch-backend/model';
import { motion } from 'motion/react';
import { ActivityNumberStepperQuestion } from './questionTypes/activity-number-stepper-question';
import { QuestionHeader } from './questionTypes/components/question-header';
import { DateQuestion } from './questionTypes/date-question';
import { DecimalQuestion } from './questionTypes/decimal-question';
import { HeightWeightQuestion } from './questionTypes/height-weight-question';
import { MultiselectQuestion } from './questionTypes/multiselect-question';
import { NumberQuestion } from './questionTypes/number-question';
import { NumberStepperQuestion } from './questionTypes/number-stepper-question';
import { SelectQuestion } from './questionTypes/select-question';
import { TextQuestion } from './questionTypes/text-question';
import { HeightWeightQuestion as HeightWeightQuestionType } from './types';
import { NameEmailQuestion } from './questionTypes/name-email-question';
import { EmailOnlyQuestion } from './questionTypes/email-only-question';

// Extended question type that includes our custom activity matrix question
type QuestionType = QuestionnaireCategoryItemsItemOneOf;

interface QuestionComponentProps {
  question: QuestionType;
  hideWhyWeAsk?: boolean;
  onWhyWeAskExpand?: () => void;
}

export const Question = ({ question, hideWhyWeAsk, onWhyWeAskExpand }: QuestionComponentProps) => {
  // Special handling for full-screen questions
  if (question.questionType === 'email-only') {
    return <EmailOnlyQuestion question={question} />;
  }

  const renderQuestionComponent = () => {
    switch (question.questionType) {
      case 'text':
        return <TextQuestion question={question} />;

      case 'number':
        return <NumberQuestion question={question} />;

      case 'decimal':
        return <DecimalQuestion question={question} />;

      case 'date':
        return <DateQuestion question={question} />;

      case 'select':
        return <SelectQuestion question={question} />;

      case 'radio':
        return <SelectQuestion question={question} />;

      case 'multiselect':
        return <MultiselectQuestion question={question} />;

      case 'checkbox':
        return <MultiselectQuestion question={question} />;

      case 'height':
        return <HeightWeightQuestion question={question} />;

      case 'weight':
        return <HeightWeightQuestion question={question} />;

      case 'number-stepper':
        return <NumberStepperQuestion question={question} />;

      case 'height-weight':
        return <HeightWeightQuestion question={question as HeightWeightQuestionType} />;

      case 'exercise-activity-matrix':
        return <ActivityNumberStepperQuestion question={question} />;

      case 'name-email':
        return <NameEmailQuestion question={question} />;

      default:
        console.warn(`Unknown question type: ${(question as QuestionType).questionType}`);
        return null;
    }
  };

  return (
    <motion.div
      key={question?.slug}
      initial={{
        opacity: 0,
        x: 20,
      }}
      animate={{
        opacity: 1,
        x: 0,
        transition: { duration: 0.5 },
      }}
      exit={{
        opacity: 0,
        x: -10,
        transition: { duration: 0.5 },
      }}
      className="questionnaire-question-layout"
    >
      {/* Pinned Header */}
      <QuestionHeader
        question={question}
        hideWhyWeAsk={hideWhyWeAsk || false}
        onWhyWeAskExpand={onWhyWeAskExpand || (() => ({}))}
      />

      {/* Question Component */}
      <div className="questionnaire-question-form">{renderQuestionComponent()}</div>
    </motion.div>
  );
};
